import { DateTools } from '@treezor/design-system';

import {
  IOperationsFilters,
  IGetOperationsReport,
  TWalletOperation,
  IOperationSimulation,
} from '@/features/wallets/store/models/walletOperation.model';
import {
  IWallet,
  IWalletCreation,
  IWalletStatementDownload,
  IWalletAccountDetailsDownload,
  ISearchWallet,
  IWalletDomiciliationCertificateDownload,
  IWalletBalanceCertificateDownload,
  IWalletClosureCertficateDownload,
  IOperationProofOfPayoutDownload,
} from '@/features/wallets/store/models/wallet.model';
import { fetchMethods } from '@/types/FetchService.types';
import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { IBalance } from '@/store/models/search.model';
import { ICursorElements } from '@/store/types/search.types';
import { ITransferP2P, IWalletTransfer } from '@/store/models/p2p.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { filtersToQueryParameters } from '@/utils/filtersToQueryParameters';
import { getConfigWalletIdsList } from '@/features/wallets/hooks/useConfigWalletIdsList';

export class WalletService {
  static queryKey = 'Wallets';
  static searchWallets = async (filters?: ISearchWallet, shouldSearchBalance: boolean = true): Promise<IWallet[]> => {
    try {
      const config = getStaticDashboardConfig();
      const params = filtersToQueryParameters<ISearchWallet>(filters);
      const walletTypeIdList = getConfigWalletIdsList();

      if (filters.userId) {
        params.set('userId', filters.userId.toString());
        walletTypeIdList.map((typeId) => params.append('walletTypeIdList[]', typeId.toString()));
      }

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'search', params: Object.fromEntries(params) }],
        `${config.api.baseUrl}/v1/wallets?${params.toString()}`,
        { headers: createHttpHeader() },
      );

      const wallets = await decryptTreezorResponse<IWallet>(response, { isSequential: true });
      if (wallets.length === 0) return wallets;

      if (shouldSearchBalance && filters) {
        try {
          if (filters.userId) {
            const balances = await WalletService.getBalancesByUserId(wallets[0].userId);
            return wallets.map((wallet) => {
              const walletBalance = balances.find((balance) => wallet.walletId === balance.walletId);
              if (walletBalance) {
                return {
                  ...wallet,
                  authorizedBalance: walletBalance.authorizedBalance,
                  solde: walletBalance.currentBalance,
                };
              }
              return wallet;
            });
          }

          if (filters.walletId) {
            const balance = await WalletService.getBalanceByWalletId(filters.walletId);
            return [{ ...wallets[0], authorizedBalance: balance.authorizedBalance, solde: balance.currentBalance }];
          }
        } catch {
          return wallets;
        }
      }
      return wallets;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static searchP2PWallets = async (search: ISearchWallet): Promise<IWallet[]> => {
    try {
      if (!search.walletId && search.userId) {
        throw new Error('walletId and userId undefined in WalletService');
      }

      if (search.userId) {
        try {
          const wallets = await this.searchWallets({ userId: search.userId });
          if (wallets) return wallets;
        } catch (error) {
          console.error(error);
        }
      }

      if (search.walletId) {
        const wallets = await this.searchWallets({ walletId: search.walletId });
        if (wallets) return wallets;
      }
      return undefined;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static searchWallet = async (walletId: number): Promise<IWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const walletResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, id: walletId, action: 'search wallet' }],
        `${config.api.baseUrl}/v1/wallets/${walletId}`,
        { headers: createHttpHeader() },
      );

      const [wallet] = await decryptTreezorResponse<IWallet>(walletResponse, { isSequential: true });
      if (!wallet) throw new Error('Wallet not found');
      try {
        const balance = await WalletService.getBalanceByWalletId(walletId);
        return { ...wallet, authorizedBalance: balance.authorizedBalance, solde: balance.currentBalance };
      } catch {
        return wallet;
      }
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getBalanceByWalletId = async (walletId: number): Promise<IBalance> => {
    try {
      const config = getStaticDashboardConfig();

      const balancesResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, id: walletId, action: 'get wallet balance' }],
        `${config.api.baseUrl}/v1/balances?walletId=${walletId}`,
        { headers: createHttpHeader() },
      );

      const [balance] = await decryptTreezorResponse<IBalance>(balancesResponse, { shouldSkipEncryption: true });
      return balance;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getBalancesByUserId = async (userId: number): Promise<IBalance[]> => {
    try {
      const config = getStaticDashboardConfig();

      const balancesResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, user: userId }],
        `${config.api.baseUrl}/v1/balances?userId=${userId}`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IBalance>(balancesResponse, { shouldSkipEncryption: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static deactivateWallet = async (walletId: number): Promise<IWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.DELETE, id: walletId }],
        `${config.api.baseUrl}/v1/wallets/${walletId}?origin=OPERATOR`,
        { headers: createHttpHeader() },
      );

      const [wallet] = await decryptTreezorResponse<IWallet>(response, { isSequential: true });
      return wallet;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static createWallet = async (walletRequest: Partial<IWalletCreation>): Promise<IWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const createWalletResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, params: walletRequest }],
        `${config.api.baseUrl}/v1/wallets/`,
        { headers: createHttpHeader(), body: JSON.stringify(walletRequest) },
      );

      const [wallet] = await decryptTreezorResponse<IWallet>(createWalletResponse);
      return wallet;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getOperations = async (
    walletId: number,
    userId: number,
    operationsFilters?: IOperationsFilters,
    cursor?: string | null,
  ): Promise<ICursorElements<TWalletOperation>> => {
    try {
      const config = getStaticDashboardConfig();

      const params = new URLSearchParams();
      params.set('walletId', walletId.toString());
      operationsFilters?.dateRange?.dateFrom &&
        params.set('dateFrom', DateTools.format(operationsFilters.dateRange.dateFrom, 'iso'));
      operationsFilters?.dateRange?.dateTo &&
        params.set('dateTo', DateTools.format(operationsFilters.dateRange.dateTo, 'iso'));

      if (operationsFilters?.amountRange?.minimum)
        params.set('amountFrom', String(Math.ceil(operationsFilters.amountRange.minimum * 100)));
      if (operationsFilters?.amountRange?.maximum)
        params.set('amountTo', String(Math.ceil(operationsFilters.amountRange.maximum * 100)));
      if (cursor) params.set('cursor', cursor.toString());
      if (operationsFilters?.direction) {
        if (operationsFilters.direction.CREDIT && !operationsFilters.direction.DEBIT) {
          params.set('direction', 'CREDIT');
        } else if (!operationsFilters.direction.CREDIT && operationsFilters.direction.DEBIT) {
          params.set('direction', 'DEBIT');
        }
      }

      const filterOperationsTypeParams = new URLSearchParams();
      const filterOperations: { operationType?: string[]; initialFlow?: string[]; status?: string[] } = {};
      if (operationsFilters?.operationType) {
        const transactionsTypes = Object.keys(operationsFilters.operationType).filter(
          (key) => operationsFilters.operationType[key] === true,
        );
        filterOperations.operationType = transactionsTypes;
        transactionsTypes.forEach((type) => filterOperationsTypeParams.append('operationType[]', type));
      }
      if (operationsFilters?.initialFlow) {
        const initialFlows = Object.keys(operationsFilters.initialFlow).filter(
          (key) => operationsFilters.initialFlow[key] === true,
        );
        filterOperations.initialFlow = initialFlows;
        initialFlows.forEach((initialFlow) => filterOperationsTypeParams.append('initialFlow[]', initialFlow));
      }

      if (operationsFilters?.status) {
        const allStatus = Object.keys(operationsFilters.status).filter((key) => operationsFilters.status[key] === true);
        filterOperations.status = allStatus;
        allStatus.forEach((status) => filterOperationsTypeParams.append('status[]', status));
      }

      const response = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.GET,
            action: 'get operations',
            params: { ...Object.fromEntries(params), ...filterOperations },
          },
        ],
        `${config.api.baseUrl}/core-connect/operations?${filterOperationsTypeParams.toString()}&${params}`,
        { headers: createHttpHeader() },
      );

      const walletOperations = await decryptTreezorResponse<TWalletOperation>(response, { userId, hasCursor: true });
      return walletOperations;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static transferP2P = async (transferP2P: IWalletTransfer, userId: number): Promise<ITransferP2P> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.POST, action: 'transferts', params: { transferP2P } }],
        `${config.api.baseUrl}/v1/transfers`,
        { headers: createHttpHeader(), body: JSON.stringify(transferP2P) },
      );

      const [transfer] = await decryptTreezorResponse<ITransferP2P>(response, { userId });
      return transfer;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getWalletAccountDetail = async (walletId: number): Promise<IWalletAccountDetailsDownload> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get account details', id: walletId }],
        `${config.api.baseUrl}/core-connect/account-details/${walletId}/computed`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IWalletAccountDetailsDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getStatement = async (walletId: number, month: string, year: string): Promise<IWalletStatementDownload> => {
    try {
      const config = getStaticDashboardConfig();
      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get statements', id: walletId, params: { month, year } }],
        `${config.api.baseUrl}/core-connect/statements/${walletId}/computed?year=${year}&month=${month}`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IWalletStatementDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getDomiciliationCertficate = async (walletId: number): Promise<IWalletDomiciliationCertificateDownload> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get domiciliation certificate', id: walletId }],
        `${config.api.baseUrl}/core-connect/certificates/walletDomiciliation/${walletId}/computed`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IWalletDomiciliationCertificateDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getBalanceCertificate = async (walletId: number): Promise<IWalletBalanceCertificateDownload> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get balance certificate', id: walletId }],
        `${config.api.baseUrl}/core-connect/certificates/walletBalance/${walletId}/computed`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IWalletBalanceCertificateDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getClosureCertificate = async (walletId: number): Promise<IWalletClosureCertficateDownload> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get account details', id: walletId }],
        `${config.api.baseUrl}/core-connect/certificates/walletClosure/${walletId}/computed`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IWalletClosureCertficateDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateWallet = async (walletToUpdate: Partial<IWalletCreation>, walletId: number): Promise<IWallet> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, id: walletId, params: walletToUpdate }],
        `${config.api.baseUrl}/v1/wallets/${walletId}`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...walletToUpdate, walletTypeId: String(walletToUpdate.walletTypeId) }),
        },
      );

      const [wallet] = await decryptTreezorResponse<IWallet>(response);
      return wallet;
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static requestOperationsReport = async (
    walletId: number,
    dates: { dateFrom: string; dateTo: string },
  ): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();
      const params = new URLSearchParams();
      params.set('dateFrom', String(dates.dateFrom));
      params.set('dateTo', String(dates.dateTo));

      const url = `${config.api.baseUrl}/core-connect/operations/${walletId}/report?${params} `;
      const headers = createHttpHeader();

      await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.POST,
            action: 'get operation report',
            id: walletId,
            params: Object.fromEntries(params),
          },
        ],
        url,
        { headers },
      );
    } catch (err) {
      return Promise.reject(err);
    }
  };
  static getOperationsReport = async (
    walletId: number,
    dates: { dateFrom: string; dateTo: string },
  ): Promise<IGetOperationsReport> => {
    try {
      const config = getStaticDashboardConfig();
      const params = new URLSearchParams();
      params.set('dateFrom', String(dates.dateFrom));
      params.set('dateTo', String(dates.dateTo));

      const url = `${config.api.baseUrl}/core-connect/operations/${walletId}/report?${params} `;
      const headers = createHttpHeader();

      const reportResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.GET,
            action: 'get operation report',
            id: walletId,
            params: Object.fromEntries(params),
          },
        ],
        url,
        { headers },
      );
      return await decryptTreezorResponse<IGetOperationsReport>(reportResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static simulateSctr = async (iban: string, amount: number): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();

      const params = new URLSearchParams();
      params.set('iban', iban);
      params.set('amount', String(amount));

      const response = await fetchQuery(
        ['sctr-simulation', { method: fetchMethods.POST }],
        `${config.api.baseUrl}/simulation/payin-sct?${params}`,
        { headers: createHttpHeader() },
      );

      await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static simulateOperation = async (data: IOperationSimulation): Promise<void> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        ['operation-simulation', { method: fetchMethods.POST }],
        `${config.api.baseUrl}/simulation/operations`,
        { headers: createHttpHeader(), body: JSON.stringify(data) },
      );
      await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getOperationProofOfPayout = async (objectId: string): Promise<IOperationProofOfPayoutDownload> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'get proof of payout', id: objectId }],
        `${config.api.baseUrl}/core-connect/payouts/${objectId}/proof`,
        { headers: createHttpHeader() },
      );

      return await decryptTreezorResponse<IOperationProofOfPayoutDownload>(response, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}

export default WalletService;
